import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Card, Icon, PageEyebrow } from '../../ui/index.js';
import type { infra } from '@photo-vault/shared';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from '../albums/albumKeys.js';
import { MediaGrid } from '../viewer/MediaGrid.js';
import { decryptRating } from '../rating/ratingCrypto.js';
import { decryptTagCached } from '../tags/tagCache.js';

interface PhotoRow {
  photo: dto.Photo;
  filename: string;
  mimeType?: string;
  url: string | null;
  rating: number | null;
}

interface Loaded {
  rootId: string;
  albumKey: CryptoKey;
  tagId: string;
  tagName: string;
  rows: PhotoRow[];
  nextCursor?: string;
  loadingMore: boolean;
}

// Viewer-mode gallery for a shared tag. Same render path as TagDetailPage,
// no edit affordances. K_album resolved from the incoming share record.
export function SharedTagDetailPage() {
  const { rootId, tagId } = useParams<{ rootId: string; tagId: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [state, setState] = useState<Loaded | 'loading' | { error: string }>('loading');

  useEffect(() => {
    if (!rootId || !tagId) return;
    let cancelled = false;
    setState('loading');
    (async () => {
      try {
        const { shares } = await metadata.listIncomingShares(session.jwt);
        const share = shares.find((s) => s.rootAlbumId === rootId);
        if (!share) {
          if (!cancelled) setState({ error: 'condivisione non trovata' });
          return;
        }
        const albumKey = await getAlbumKey(rootId, share.wrappedAlbumKey, session.rsaPriv);
        const [tagDef, firstPage] = await Promise.all([
          metadata.getTag(tagId, rootId, session.jwt),
          metadata.listTagPhotos(tagId, rootId, session.jwt),
        ]);
        if (cancelled) return;

        const decryptedTag = await decryptTagCached(tagDef, rootId, albumKey);
        const rows = await hydratePage(
          metadata,
          firstPage.photos,
          albumKey,
          session.jwt,
          () => cancelled,
        );
        if (cancelled) return;

        setState({
          rootId,
          albumKey,
          tagId,
          tagName: decryptedTag.name,
          rows,
          ...(firstPage.nextCursor ? { nextCursor: firstPage.nextCursor } : {}),
          loadingMore: false,
        });
      } catch (err) {
        if (!cancelled) setState({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rootId, tagId, session.jwt, session.rsaPriv, metadata]);

  if (!rootId || !tagId) return null;
  if (state === 'loading') return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  if ('error' in state) return <p className="py-6 text-sm text-error">Errore: {state.error}</p>;

  async function loadMore() {
    if (typeof state !== 'object' || 'error' in state) return;
    if (!state.nextCursor || state.loadingMore) return;
    setState({ ...state, loadingMore: true });
    try {
      const page = await metadata.listTagPhotos(
        state.tagId,
        state.rootId,
        session.jwt,
        state.nextCursor,
      );
      const more = await hydratePage(
        metadata,
        page.photos,
        state.albumKey,
        session.jwt,
        () => false,
      );
      setState((curr) => {
        if (typeof curr !== 'object' || 'error' in curr) return curr;
        const next: Loaded = {
          ...curr,
          rows: [...curr.rows, ...more],
          loadingMore: false,
        };
        if (page.nextCursor) next.nextCursor = page.nextCursor;
        else delete (next as { nextCursor?: string }).nextCursor;
        return next;
      });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
      setState((curr) =>
        typeof curr === 'object' && !('error' in curr) ? { ...curr, loadingMore: false } : curr,
      );
    }
  }

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <PageEyebrow>viewer · solo lettura</PageEyebrow>
        <Breadcrumb rootId={state.rootId} />
        <h2 className="font-display text-2xl font-semibold">{state.tagName}</h2>
        <p className="text-sm text-on-surface-variant">
          {state.rows.length === 0
            ? 'Nessuna foto con questo tag.'
            : state.rows.length === 1
              ? '1 foto'
              : `${state.rows.length} foto`}
          {state.nextCursor ? ' (parziale)' : null}
        </p>
      </div>

      {state.rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center">
          <Icon name="photo_library" className="text-4xl text-on-surface-variant" />
          <p className="text-sm text-on-surface-variant">Nessuna foto associata a questo tag.</p>
        </Card>
      ) : (
        <MediaGrid
          items={state.rows.map((r) => ({
            id: r.photo.id,
            url: r.url,
            filename: r.filename,
            ...(r.mimeType ? { mimeType: r.mimeType } : {}),
            rating: r.rating,
            info: {
              encryptedMeta: r.photo.encryptedMeta,
              uploadedAt: r.photo.createdAt,
              encryptedSize: r.photo.size,
              ...(r.photo.keyGen !== undefined ? { keyGen: r.photo.keyGen } : {}),
            },
          }))}
          albumKey={state.albumKey}
        />
      )}

      {state.nextCursor ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => void loadMore()}
            loading={state.loadingMore}
            leftIcon={<Icon name="expand_more" />}
          >
            Carica altri
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Breadcrumb({ rootId }: { rootId: string }) {
  return (
    <nav className="flex flex-wrap items-center gap-2 text-sm">
      <Link to="/shared" className="text-on-surface-variant transition-colors hover:text-cyan">
        Condivisi
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <Link
        to={`/shared/${encodeURIComponent(rootId)}`}
        className="text-on-surface-variant transition-colors hover:text-cyan"
      >
        Radice
      </Link>
      <Icon name="chevron_right" className="text-base text-on-surface-variant" />
      <Link
        to={`/shared/${encodeURIComponent(rootId)}/tags`}
        className="text-on-surface-variant transition-colors hover:text-cyan"
      >
        Tag
      </Link>
    </nav>
  );
}

async function hydratePage(
  metadata: infra.MetadataStore,
  photos: dto.Photo[],
  albumKey: CryptoKey,
  jwt: string,
  isCancelled: () => boolean,
): Promise<PhotoRow[]> {
  if (photos.length === 0) return [];
  const decryptedMeta = await Promise.all(
    photos.map(async (p) => {
      const ratingPromise = p.encryptedRating
        ? decryptRating(p.encryptedRating, albumKey)
        : Promise.resolve<number | null>(null);
      try {
        const json = await cv.decryptText(p.encryptedMeta, albumKey);
        const meta = JSON.parse(json) as { filename?: string; mimeType?: string };
        const rating = await ratingPromise;
        return { photo: p, filename: meta.filename ?? p.id, mimeType: meta.mimeType, rating };
      } catch {
        const rating = await ratingPromise;
        return { photo: p, filename: '(meta non leggibile)', rating };
      }
    }),
  );
  const rows: PhotoRow[] = decryptedMeta.map((m) => ({
    photo: m.photo,
    filename: m.filename,
    ...('mimeType' in m && m.mimeType ? { mimeType: m.mimeType } : {}),
    url: null,
    rating: m.rating,
  }));
  rows.sort((a, b) => a.photo.createdAt.localeCompare(b.photo.createdAt));

  const urlByPhotoId = new Map<string, string>();
  await metadata.downloadsBatchAllStreaming(
    photos.map((p) => ({ albumId: p.albumId, photoId: p.id })),
    jwt,
    (chunk) => {
      if (isCancelled()) return;
      for (const it of chunk) urlByPhotoId.set(it.photoId, it.url);
    },
  );
  for (const r of rows) {
    const u = urlByPhotoId.get(r.photo.id);
    if (u) r.url = u;
  }
  return rows;
}
